import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlass, Image, ArrowRight, CaretRight, Globe, LockSimple } from '@phosphor-icons/react';
import { getCountry } from 'countries-and-timezones';

import { Button } from '../ui/button';
import { Collection, Suggestion, AttachedFile } from './types';
import { AttachmentThumbnails } from './AttachmentThumbnails';
import { IncludePageToggle } from './IncludePageToggle';

import { Combobox } from '@/components/combobox';
import { TwoLevelCombobox } from '@/components/two-level-combobox';
import { ComboboxItem } from '@/components/combobox';
import { cn } from '@/lib/utils';
import { useBooleanFlagValue } from '@/lib/feature-flags';

export const PrimarySearch = React.forwardRef<
  HTMLTextAreaElement,
  {
    query: string;
    onQueryChange: (query: string) => void;
    onSearch: () => void;
    textConfig: {
      searchPlaceholder?: string;
      attachImageText?: string;
      allDocumentsText?: string;
    };
    collections?: Collection[];
    currentCollectionId?: string;
    onCollectionChange?: (collectionId: string) => void;
    allowedRegions?: string[];
    currentRegion?: string;
    onRegionChange?: (region: string) => void;
    apiKey?: string;
    fetchRegionConfig?: (
      apiKey: string,
      region: string,
    ) => Promise<{ settings?: { collections?: Collection[] } } | null>;
    enableFileAttachment?: boolean;
    attachedFiles?: AttachedFile[];
    isUploading?: boolean;
    onAddFiles?: (files: File[]) => void;
    onRemoveFile?: (id: string) => void;
    isSearching?: boolean;
    autocompleteSuggestions?: Suggestion[];
    onAutocompleteSuggestionSelect?: (suggestion: Suggestion) => void;
    isAgentAssist?: boolean;
    includePageEnabled?: boolean;
    includePageLoading?: boolean;
    includePageDisabled?: boolean;
    onIncludePageToggle?: (enabled: boolean) => Promise<void>;
  }
>(
  (
    {
      query,
      onQueryChange,
      onSearch,
      textConfig,
      collections = [],
      currentCollectionId,
      onCollectionChange,
      allowedRegions = [],
      onRegionChange,
      apiKey,
      fetchRegionConfig,
      enableFileAttachment = false,
      attachedFiles = [],
      isUploading = false,
      onAddFiles,
      onRemoveFile,
      isSearching = false,
      autocompleteSuggestions = [],
      onAutocompleteSuggestionSelect,
      isAgentAssist = false,
      includePageEnabled = false,
      includePageLoading = false,
      includePageDisabled = false,
      onIncludePageToggle,
    },
    ref,
  ) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    const handlePaste = (e: React.ClipboardEvent) => {
      if (!enableFileAttachment || !onAddFiles) return;
      const files = Array.from(e.clipboardData.files);
      if (files.length > 0) {
        e.preventDefault();
        onAddFiles(files);
      }
    };

    const handleDrop = (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (!enableFileAttachment || !onAddFiles) return;
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onAddFiles(files);
      }
    };

    const handleDragOver = (e: React.DragEvent) => {
      e.preventDefault();
      if (enableFileAttachment) setIsDragOver(true);
    };

    const handleDragLeave = () => {
      setIsDragOver(false);
    };

    // Get feature flag for showing include page toggle
    const showIncludePageToggle = useBooleanFlagValue('showIncludePageToggle', false);

    // Create region items for TwoLevelCombobox
    const regionItems = [
      {
        value: 'all',
        label: 'All regions',
        icon: <Globe className="size-4" />,
      },
      ...allowedRegions.map((region) => {
        const country = getCountry(region);

        return {
          value: region,
          label: country?.name || region,
          icon: (
            <img
              alt={`${country?.name || region}`}
              src={`https://purecatamphetamine.github.io/country-flag-icons/3x2/${region}.svg`}
              className="w-5"
            />
          ),
        };
      }),
    ];

    // Function to fetch collections for a selected region
    const fetchCollectionsForRegion = React.useCallback(
      async (regionId: string): Promise<ComboboxItem[]> => {
        if (!apiKey || !fetchRegionConfig) {
          console.warn('No apiKey or fetchRegionConfig provided, falling back to current collections');

          return collections.map((collection) => ({
            value: collection.id,
            label: collection.name,
            icon: collection.siteEnabled ? <Globe className="size-4" /> : <LockSimple className="size-4" />,
          }));
        }

        try {
          const regionConfig = await fetchRegionConfig(apiKey, regionId);
          const regionCollections = regionConfig?.settings?.collections || [];

          return regionCollections.map((collection) => ({
            value: collection.id,
            label: collection.name,
            icon: collection.siteEnabled ? <Globe className="size-4" /> : <LockSimple className="size-4" />,
          }));
        } catch (error) {
          console.error('Error fetching collections for region:', error);

          // Fallback to current collections on error
          return collections.map((collection) => ({
            value: collection.id,
            label: collection.name,
            icon: collection.siteEnabled ? <Globe className="size-4" /> : <LockSimple className="size-4" />,
          }));
        }
      },
      [apiKey, fetchRegionConfig, collections],
    );

    const handleSelectionChange = (region: ComboboxItem | null, collection: ComboboxItem | null) => {
      if (region && onRegionChange) {
        onRegionChange(region.value);
      }
      if (collection && onCollectionChange) {
        onCollectionChange(collection.value);
      }
    };

    const adjustTextareaHeight = (textarea: HTMLTextAreaElement) => {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !isUploading) {
        e.preventDefault();
        onSearch();
      }
    };

    // Close suggestions when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (!(event.target as Element).closest('.suggestions-dropdown')) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);

      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Show suggestions only when we have a query
    useEffect(() => {
      if (query.trim()) {
        setShowSuggestions(true);
      }
    }, [query]);

    const shouldShowSuggestions = showSuggestions && query.trim() && autocompleteSuggestions.length > 0;

    // Animation variants for consistent animations
    const dropdownVariants = {
      hidden: {
        opacity: 0,
        scale: 0.95,
        transformOrigin: 'top',
      },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          type: 'spring',
          stiffness: 500,
          damping: 30,
          mass: 0.5,
        },
      },
      exit: {
        opacity: 0,
        scale: 0.95,
        transition: {
          duration: 0.15,
          ease: 'easeOut',
        },
      },
    };

    const itemVariants = {
      hidden: {
        opacity: 0,
        x: -4,
      },
      visible: {
        opacity: 1,
        x: 0,
        transition: {
          duration: 0.2,
        },
      },
    };

    const listVariants = {
      visible: {
        transition: {
          staggerChildren: 0.03,
          delayChildren: 0.05,
        },
      },
    };

    return (
      <div className="relative w-full" data-name="PrimarySearch">
        <div
          className={cn(
            'relative w-full overflow-hidden bg-background border border-border transition-all',
            shouldShowSuggestions ? 'rounded-t-md' : 'rounded-md',
            isDragOver && enableFileAttachment && 'ring-2 ring-primary',
          )}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onPaste={handlePaste}
        >
          {isDragOver && enableFileAttachment && (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center rounded-md"
              style={{ backgroundColor: 'color-mix(in srgb, var(--bfc-card) 90%, transparent)' }}
            >
              <p className="text-sm font-medium text-muted-foreground">Drop images here to attach</p>
            </div>
          )}
          {/* Attached files */}
          {enableFileAttachment && (
            <AttachmentThumbnails
              items={attachedFiles.map((f) => ({ id: f.id, src: f.preview, url: f.publicUrl, status: f.status }))}
              size="md"
              onRemove={onRemoveFile}
              className="pt-3 px-3 bg-card"
            />
          )}

          <div className="p-3 flex items-center bg-card" data-name="search-input">
            <MagnifyingGlass className="h-5 w-5 mr-2 text-muted-foreground" aria-hidden role="presentation" />
            <textarea
              ref={ref}
              value={query}
              onChange={(e) => {
                onQueryChange(e.target.value);
                adjustTextareaHeight(e.target);
              }}
              onKeyDown={handleKeyPress}
              onFocus={() => setShowSuggestions(true)}
              placeholder={textConfig.searchPlaceholder}
              className={cn(
                'w-full resize-none !outline-none text-base py-1 min-h-9',
                'overflow-hidden border-none bg-transparent shadow-none',
                'placeholder:text-gray-400 text-dark-900 !ring-0',
              )}
              rows={1}
            />
            {isSearching && (
              <div className="flex items-center justify-center ml-2">
                <div className="animate-spin rounded-full size-4 border-2 border-t-transparent border-primary" />
              </div>
            )}
          </div>

          {!shouldShowSuggestions && (
            <div className="flex items-center justify-between px-3 py-2 border-t border-border">
              <div className="flex items-center gap-2">
                {/* File attachment button */}
                {enableFileAttachment && (
                  <>
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files) {
                          onAddFiles?.(Array.from(e.target.files));
                        }
                        // Reset value so the same file can be selected again
                        e.target.value = '';
                      }}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="rounded-md size-8 hover:bg-opacity-80 disabled:opacity-50"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={attachedFiles.length >= 5}
                      aria-label={textConfig.attachImageText}
                    >
                      <Image weight="regular" className="size-4 text-secondary-foreground" />
                    </Button>
                  </>
                )}

                <div className="flex gap-1" data-name="collections-dropdown">
                  {allowedRegions.length > 0 && collections.length > 0 ? (
                    // Two-level selection: Region -> Collection
                    <TwoLevelCombobox
                      firstLevelItems={regionItems}
                      fetchSecondLevelItems={fetchCollectionsForRegion}
                      onSelectionChange={handleSelectionChange}
                      firstLevelLabel="Region"
                      secondLevelLabel="Collection"
                      firstLevelPlaceholder="All documents"
                      className="min-w-[200px]"
                      displayLabel={false}
                    />
                  ) : collections.length > 0 ? (
                    // Single-level selection: Collection only (fallback when no regions)
                    <Combobox
                      options={collections.map((collection) => ({
                        value: collection.id,
                        label: collection.name,
                        icon: collection.siteEnabled ? <Globe className="size-4" /> : <LockSimple className="size-4" />,
                      }))}
                      value={currentCollectionId === 'all' ? undefined : currentCollectionId}
                      defaultValueLabel="All documents"
                      placeholder="Select collection"
                      noResultsLabel="No collections found"
                      dropdownWidth={200}
                      onChange={(value) => onCollectionChange?.(value)}
                    />
                  ) : null}
                </div>

                {/* Include page toggle for agent assist mode */}
                {isAgentAssist && onIncludePageToggle && showIncludePageToggle && (
                  <IncludePageToggle
                    value={includePageEnabled}
                    loading={includePageLoading}
                    disabled={includePageDisabled}
                    onToggle={onIncludePageToggle}
                  />
                )}
              </div>

              <div className="flex items-center" data-name="search-button">
                <Button
                  size="sm"
                  disabled={!query || isSearching || isUploading}
                  aria-disabled={!query || isSearching || isUploading}
                  className="rounded-md size-8 disabled:opacity-50 bg-primary"
                  onClick={onSearch}
                >
                  {isSearching ? (
                    <div className="flex items-center" role="status" aria-live="polite">
                      <div
                        className="animate-spin rounded-full size-4 border-2 border-t-transparent border-white"
                        aria-hidden="true"
                      />
                      <span className="sr-only">Loading</span>
                    </div>
                  ) : (
                    <ArrowRight
                      className="text-primary-foreground"
                      size={16}
                      weight="bold"
                      aria-label="Submit question"
                    />
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>

        <AnimatePresence mode="wait">
          {shouldShowSuggestions && (
            <motion.div
              variants={dropdownVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={cn(
                'suggestions-dropdown absolute',
                'left-0 right-0 z-50',
                'overflow-hidden bg-card',
                'border-border border border-t-0 rounded-b-md shadow-md -mt-px',
                'md:relative',
              )}
            >
              {/* Current query suggestion */}
              <motion.div variants={itemVariants}>
                {query.trim() && (
                  <div
                    role="option"
                    aria-selected={false}
                    tabIndex={0}
                    className={cn(
                      'px-4 py-3',
                      'cursor-pointer',
                      'flex items-center gap-3',
                      'transition-colors duration-200',
                      'hover:bg-dark-100/50 text-foreground bg-dark-200',
                      'border-b border border-border',
                    )}
                    onClick={() => {
                      onSearch();
                      setShowSuggestions(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSearch();
                        setShowSuggestions(false);
                      }
                    }}
                  >
                    <MagnifyingGlass className="size-4 flex-shrink-0 text-secondary-foreground" />
                    <span className="text-[15px]">{query}</span>
                    <div className="ml-auto flex items-center">
                      <CaretRight className="size-4 text-secondary-foreground" />
                    </div>
                  </div>
                )}
              </motion.div>

              <motion.div variants={listVariants} initial="hidden" animate="visible">
                {autocompleteSuggestions.map((suggestion) => (
                  <motion.div
                    key={suggestion.id}
                    variants={itemVariants}
                    role="option"
                    aria-selected={false}
                    tabIndex={0}
                    className="px-4 py-3 cursor-pointer flex items-center gap-3 transition-colors duration-200 hover:bg-dark-300 text-foreground"
                    onClick={() => {
                      onAutocompleteSuggestionSelect?.(suggestion);
                      setShowSuggestions(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onAutocompleteSuggestionSelect?.(suggestion);
                        setShowSuggestions(false);
                      }
                    }}
                  >
                    <MagnifyingGlass className="size-4 flex-shrink-0 text-secondary-foreground" />
                    <span className="text-[15px]">{suggestion.question}</span>
                    <div className="ml-auto flex items-center">
                      <CaretRight className="size-4 text-secondary-foreground" />
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  },
);

PrimarySearch.displayName = 'PrimarySearch';

export default PrimarySearch;
